import { forwardRef, useCallback, useLayoutEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';

import { Field } from '@base-ui/react/field';
import { IconButton } from 'components/Button';
import { cn } from 'components/utils';

import {
  textAreaBoxRecipe,
  textAreaControlRecipe,
  textAreaCounterRecipe,
  textAreaHelperRecipe,
  textAreaLabelRecipe,
  textAreaRootRecipe,
  textAreaSubLabelRecipe,
  textAreaTrailingButtonRecipe,
} from './TextArea.recipe';

import type { ControlProps, TextAreaProps } from './types';

/**
 * Design System 2.0 multi-line text input.
 *
 * Ported from the Figma Text Area matrix (file 5aSkE0U4TeWbuIZDvccHjc, node 1437:128678, page
 * "Text-area [In review]") — 48 cells. That set REPLACED node 1020-25860, which this component was
 * first built from, and the rebuild moved almost everything: the fill, the focused edge, the height
 * floor, the label's offsets and scale, and it added a trailing button and a scrollbar that were
 * not in the retired set at all. `TextArea.recipe` has the state matrix and the per-decision
 * reasoning.
 *
 * Props mirror the Figma axes where they are real inputs rather than depictions of state:
 *
 *   `position`    the `Position` axis (Standalone / First / Middle / Last), which the rebuilt set
 *                 draws in full — `middle` is no longer extrapolated
 *   `error`       the `hasError` axis
 *   `disabled`    the `isDisabled` axis
 *   `rightButton` the `rightButton` boolean, default off, drawn as an ExtraSmall Muted icon-only
 *                 Button — so it is rendered as a real `IconButton` and takes a label and a handler
 *                 alongside the glyph
 *   `helperText`  the `Helper text` boolean, plus whichever of `↪ Helper Text` / `↪ Error text`
 *                 applies — the same layer carries both strings, so ONE prop feeds both and `error`
 *                 decides which role it plays
 *   `maxLength`   the `hasCounter` boolean and `↪ Counter`, right-aligned on the sub-label row. One
 *                 prop rather than two, because a cap the user cannot see is the defect it closes
 *
 * `maxRows` has no axis at all: auto-growth is behaviour, not a drawn state, and the set records it
 * in its description instead. Turning it on drops the resize grabber — see `textAreaControlRecipe`.
 *
 * The sub-label row is opened by EITHER of them, so a counter-only field is a real shape — three of
 * the call sites this was built for cap length and pass no helper text. Figma reaches the same four
 * combinations with a third boolean (`hasSubLabel` gates the row, the other two fill it), because it
 * cannot express an OR: an auto-layout frame whose children are all hidden keeps its height rather
 * than collapsing. Here the condition is just `hasHelper || hasCounter` and no third prop is needed.
 *
 * `isFilled` and `State` are NOT props. Both are observed from the DOM, because Base UI's `Field`
 * already tracks them.
 *
 * Theme is not a prop either. Colours resolve from the nearest `.dark` ancestor through the token
 * layer, so the same markup is correct in both themes — and `.ds-textarea` needs no dark rule at all
 * to achieve that, because every one of its slots names a semantic role.
 *
 * The floating label rises 4px and shrinks to 12px once the field is filled or focused. Both
 * numbers are this component's own and neither is transferable from `TextInput`, which moves its
 * label 10px — see `textAreaLabelRecipe` for why a multi-line field's label has less room to fall.
 *
 * `position` is normally supplied by `TextInputGroup` rather than set by hand.
 *
 * ── One layer of the design is not drawn ───────────────────────────────────────────────────────
 *
 * The `Focus` column's keyboard-only halo. CSS cannot express "arrived by keyboard" on a text
 * control, and this component used to claim it could — `textAreaBoxRecipe` has the measurement and
 * what it would take. The accent border carries the focus state on its own, above both relevant
 * WCAG floors.
 *
 * ── Why the control is a `render` prop and not a Base UI part ──────────────────────────────────
 *
 * Base UI ships no multi-line field. `Field.Control` renders an `<input>` and its props type
 * extends an `'input'`'s, but `render` is typed to accept any element, and every prop the part
 * merges — `id`, `disabled`, `name`, `ref`, `aria-labelledby`, `value`/`defaultValue` and its four
 * handlers — is equally valid on a `<textarea>`. That is permissiveness rather than documented
 * support: no official example does this, and the library's own `Enter` handling is written as a
 * `tagName === 'INPUT'` guard, which could have been either an intent or an accident.
 *
 * So it was verified before it was built on, rather than after. The whole of `Field`'s state
 * machine survives the substitution — `data-filled`, `data-focused`, `data-dirty`, `data-touched`
 * and `data-invalid` all land on the root, `aria-describedby` is assembled from the description and
 * the error, and `Enter` inserts a newline instead of being swallowed. Those are asserted in this
 * component's own test file, which is where a future Base UI upgrade will trip over a regression in
 * any of them.
 *
 * One mechanical consequence shows up below: `rows` cannot go on `Field.Control`, because that
 * part's props are an `<input>`'s and an input has no rows. It rides the render element, where
 * `useRenderElement` merges it through.
 *
 * The control is the FIRST child and pays all the padding itself, so the `<textarea>` fills the box
 * and the entire field is a click target. The label and the trailing button are overlaid on top of
 * it — see the note at the top of `TextArea.recipe`. Reordering these children, or moving the
 * padding onto the box, turns the field's whole left and right gutter into dead space.
 */
export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      label,
      position,
      rightButton,
      rightButtonLabel,
      onRightButtonClick,
      helperText,
      error,
      disabled,
      rows = 3,
      maxRows,
      maxLength,
      value,
      defaultValue,
      onChange,
      className,
      rootClassName,
      ...rest
    },
    ref
  ) => {
    const hasHelper = Boolean(helperText);
    const hasCounter = maxLength !== undefined;
    const hasSubLabel = hasHelper || hasCounter;
    const autoGrow = maxRows !== undefined;

    /**
     * The control, needed here as well as by the caller — autosize measures it. Merged rather than
     * chosen between, so passing a `ref` does not silently disable the growth.
     */
    const controlRef = useRef<HTMLTextAreaElement | null>(null);
    const attachControl = useCallback(
      (node: HTMLTextAreaElement | null) => {
        controlRef.current = node;

        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          // `forwardRef` types the object form as a `RefObject` with a readonly `current`, but the
          // object React hands a forwarding component is a `MutableRefObject` — writing to it is
          // the documented way to fulfil the forward. The cast states that, and is the same one
          // every ref-merging component in this layer makes.
          (ref as MutableRefObject<HTMLTextAreaElement | null>).current = node;
        }
      },
      [ref]
    );

    /**
     * The character count.
     *
     * A controlled field is read straight off `value` — keeping a copy in state would let the two
     * disagree for a render whenever a parent rejects or rewrites an edit, which is precisely what
     * a length-capped field's parent tends to do. An uncontrolled one has no such source, so the
     * count is tracked here and seeded from `defaultValue`.
     *
     * `useState` with an initialiser rather than a plain expression: `defaultValue` is only read on
     * the first render by definition, and re-measuring it afterwards would fight an uncontrolled
     * field's own state.
     */
    const isControlled = value !== undefined;
    const [uncontrolledCount, setUncontrolledCount] = useState(() => String(defaultValue ?? '').length);
    const count = isControlled ? String(value ?? '').length : uncontrolledCount;
    const atLimit = maxLength !== undefined && count >= maxLength;

    /**
     * Auto-growth, and the two things that make it correct rather than merely working.
     *
     * The height is reset to `auto` before `scrollHeight` is read, or the element reports the
     * height it was last given and the box can only ever grow — the classic autosize bug, which
     * shows up as a field that refuses to shrink when text is deleted. `ChatInput` carries the same
     * note for the same reason.
     *
     * The cap is computed from the element's OWN line height and padding rather than from
     * constants, so it tracks the recipe: `pt-[28px]` and the `hasSubLabel` bottom inset both move,
     * and a hard-coded ceiling would quietly stop matching `maxRows` lines the first time either
     * did. Tailwind sets `box-sizing: border-box`, so `scrollHeight` and the cap are measured in the
     * same units; the border lives on the box, not on this element, so neither figure includes it.
     *
     * A `scrollHeight` of 0 means there is no layout engine (happy-dom reports every box as 0), not
     * that there is no content — the height is left alone, and the growth is proved in a `play`
     * story against real Chromium instead.
     */
    const resize = useCallback(() => {
      const el = controlRef.current;

      if (!el || maxRows === undefined) return;

      el.style.height = 'auto';

      const { scrollHeight } = el;

      if (scrollHeight === 0) return;

      const styles = getComputedStyle(el);
      const lineHeight = Number.parseFloat(styles.lineHeight);
      const insets = Number.parseFloat(styles.paddingTop) + Number.parseFloat(styles.paddingBottom);

      // `line-height: normal` has no number to multiply, so there is no cap to compute and the
      // field grows with its content — the same "leave it alone" call the zero-check above makes.
      const cap = Number.isFinite(lineHeight) ? maxRows * lineHeight + insets : Infinity;

      el.style.height = `${Math.min(scrollHeight, cap)}px`;
    }, [maxRows]);

    /**
     * `useLayoutEffect` rather than `useEffect` so the new height lands before paint; with
     * `useEffect` a fast typist sees the box lag a frame behind the caret at every line break.
     *
     * `value` is a dependency so a CONTROLLED field re-measures when its parent changes the text
     * from outside — a reset button, a template being applied — which the change handler below
     * never sees.
     */
    useLayoutEffect(() => {
      resize();
    }, [resize, value]);

    /**
     * Width changes re-wrap the text and therefore change its height, and nothing else here would
     * notice: a sidebar opening or a window resize fires no change event.
     */
    useLayoutEffect(() => {
      const el = controlRef.current;

      if (!el || maxRows === undefined || typeof ResizeObserver === 'undefined') return;

      const observer = new ResizeObserver(resize);
      observer.observe(el);

      return () => observer.disconnect();
    }, [maxRows, resize]);

    /**
     * Typed from `Field.Control` rather than as a `<textarea>` event, which is the same seam the
     * header describes: the part's props are an `<input>`'s, so both this handler and the caller's
     * `onChange` are input-shaped. Nothing here depends on the difference — `target.value` is a
     * string on either element — and re-typing it would only mean casting on the way back out.
     */
    const handleChange = useCallback<NonNullable<ControlProps['onChange']>>(
      (event) => {
        if (!isControlled) {
          setUncontrolledCount(event.target.value.length);
        }

        resize();
        onChange?.(event);
      },
      [isControlled, onChange, resize]
    );
    /**
     * Narrowed as a pair, exactly as `TextInput` and `Alert` narrow theirs, and for the same reason:
     * destructuring a discriminated union loses the correlation between its members, so each one
     * comes out independently optional. Re-pairing them is what lets `IconButton`'s required
     * `aria-label: string` be satisfied without an assertion.
     *
     * Two checks rather than three. `onRightButtonClick` rides along untested because the type
     * already guarantees it arrives with the other two — and testing it is not merely redundant but
     * an ERROR: `tsc` reports TS2774 on a truthiness check against an always-defined function,
     * reading it as a missing call.
     */
    const trailing = rightButton && rightButtonLabel ? { rightButton, rightButtonLabel, onRightButtonClick } : null;

    return (
      // `invalid` is what puts `data-invalid` on the root and `aria-invalid` on the control; every
      // red token below keys off it, so the visual and the announced state cannot drift apart.
      <Field.Root className={cn(textAreaRootRecipe(), rootClassName)} disabled={disabled} invalid={error}>
        {/* `data-part` names the bordered box so a parent can target it and a test can assert on it
            without depending on a styling class. The slot classes sit on the root. */}
        <div className={cn(textAreaBoxRecipe({ position, hasSubLabel }), className)} data-part="box">
          {/**
           * `rows` is on the render element rather than in `{...rest}`, which is the one thing about
           * this composition that is not interchangeable — see the note above. Three lines by
           * default: a multi-line field that opens at one row is a single-line field with a
           * grabber. The set's 64px content floor is a separate guarantee underneath it, so a
           * caller asking for fewer rows still gets the box the design draws.
           */}
          <Field.Control
            ref={attachControl}
            className={textAreaControlRecipe({
              hasSubLabel,
              hasRightButton: Boolean(trailing),
              autoGrow,
            })}
            defaultValue={defaultValue}
            maxLength={maxLength}
            onChange={handleChange}
            render=<textarea rows={rows} />
            value={value}
            {...rest}
          />

          <Field.Label className={textAreaLabelRecipe({ hasRightButton: Boolean(trailing) })}>{label}</Field.Label>

          {/**
           * A real `IconButton`, not a styled glyph, because the design draws a Button here and a
           * Button is what has hover and pressed cells. `2xs` is the 24px circle — Figma's
           * `ExtraSmall`, which is NOT the code's `xs`; see `textAreaTrailingButtonRecipe`, which
           * also has why this one is top-aligned where `TextInput`'s is centred.
           *
           * `type="button"` is explicit: this control sits inside a field that is usually inside a
           * form, and the browser default of `submit` would make a dictation toggle submit it.
           */}
          {trailing && (
            <IconButton
              appearance="muted"
              aria-label={trailing.rightButtonLabel}
              className={textAreaTrailingButtonRecipe()}
              data-part="trailing-button"
              disabled={disabled}
              fill="default"
              onClick={trailing.onRightButtonClick}
              size="2xs"
              type="button"
            >
              {trailing.rightButton}
            </IconButton>
          )}

          {/**
           * Helper text renders inside the border, which is what keeps this field stackable — see
           * `textAreaHelperRecipe`.
           *
           * `Field.Error` needs `match` to render off a caller-supplied flag; without it, it renders
           * only for Base UI's own constraint validation, which this component does not drive. The
           * non-error case uses `Field.Description`. Either way the text is wired into
           * `aria-describedby`, so it is announced rather than merely visible.
           *
           * Neither part is a live region, and that is Base UI's behaviour rather than a choice made
           * here — `FieldError` sets no `role="alert"` and no `aria-live`. So an error that appears
           * after first paint is correctly ASSOCIATED immediately but is not necessarily spoken
           * until the field is next visited. `TextInput` has exactly the same property; a caller
           * that needs the announcement sooner owns the live region, because only the caller knows
           * whether one error or twelve just appeared.
           */}
          {hasSubLabel && (
            <div className={textAreaSubLabelRecipe()} data-part="sub-label">
              {hasHelper && error && (
                <Field.Error className={textAreaHelperRecipe()} match>
                  {helperText}
                </Field.Error>
              )}
              {hasHelper && !error && (
                <Field.Description className={textAreaHelperRecipe()}>{helperText}</Field.Description>
              )}

              {/**
               * `aria-hidden`, and the count is carried to assistive tech by the control's own
               * `maxLength` instead — screen readers announce the limit from the attribute when the
               * field is entered.
               *
               * Reading it out as text as well would make every keystroke a live announcement of a
               * number nobody asked for, which is the standard way a counter turns from a help into
               * a barrier. The visible readout is for the sighted user who wants to glance at how
               * much room is left; the attribute is for everyone else, and it says the same thing
               * once instead of a thousand times.
               */}
              {hasCounter && (
                <span aria-hidden className={textAreaCounterRecipe({ atLimit })} data-part="counter">
                  {count}/{maxLength}
                </span>
              )}
            </div>
          )}
        </div>
      </Field.Root>
    );
  }
);
